import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Add } from "@mui/icons-material";
import { TextField, Button, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getIvrDistributionPolicy, addEditIvrDistributionPolicy } from "../../../services/ivrModule/policyServices.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { POLICY_STRINGS, POLICY_KEYS, POLICY_MESSAGES, POLICY_ERRORS, POLICY_TABLE_COLUMNS } from "../../../common/constants/ivrModule/policyConstants.js";
import PolicyTable from "./PolicyTable.jsx";
import AddEditPolicyDialog from "./AddEditPolicyDialog.jsx";

const Policy = () => {
    const [page, setPage] = useState(0);
    const [ivrPolicy, setIvrPolicy] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isEditPolicy, setIsEditPolicy] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState({});
    const [loadingPolicy, setLoadingPolicy] = useState(true);
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [searchIvrPolicy, setSearchIvrPolicy] = useState("");
    const [orderBy, setOrderBy] = useState(POLICY_KEYS.POLICY_NAME);
    const [isAddEditPolicyModalOpen, setIsAddEditPolicyModalOpen] = useState(false);

    useEffect(() => { fetchPolicy(); }, []);

    const fetchPolicy = async () => {
        try {
            setLoadingPolicy(true);
            setIvrPolicy(await getIvrDistributionPolicy());
        }
        catch (error) {
            console.error(POLICY_ERRORS.FETCH_POLICY_ERROR, error);
        }
        finally {
            setLoadingPolicy(false);
        }
    };

    const { policyToDisplay, filteredPolicyLength } = useMemo(() => {
        const filteredPolicy = ivrPolicy.filter((ivrPolicy) =>
            ivrPolicy.policyname.trim().toLowerCase().includes(searchIvrPolicy.toLowerCase())
        );
        return {
            policyToDisplay: filteredPolicy
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredPolicyLength: filteredPolicy.length,
        };
    }, [ivrPolicy, searchIvrPolicy, order, orderBy, page, rowsPerPage]);

    const handleAddEditPolicyClick = (policy = {}) => {
        setIsEditPolicy(!!policy.id);
        setSelectedPolicy(policy);
        setIsAddEditPolicyModalOpen(true);
    };

    const handleSavePolicy = async () => {
        if (!selectedPolicy) return;
        const policyToSave = {
            id: selectedPolicy.id || "",
            policyname: selectedPolicy.policyname || "",
            modeKind: selectedPolicy?.modeKind || "longestIdle",
            offerExpiresAfterSeconds: selectedPolicy?.offerExpiresAfterSeconds ?? 60,
            minConcurrentOffers: selectedPolicy?.minConcurrentOffers ?? 1,
            maxConcurrentOffers: selectedPolicy?.maxConcurrentOffers ?? 3,
        };
        try {
            await addEditIvrDistributionPolicy(policyToSave);
            setIsAddEditPolicyModalOpen(false);
            setSuccessMessage(isEditPolicy ? POLICY_MESSAGES.POLICY_UPDATED : POLICY_MESSAGES.POLICY_ADDED);
            await fetchPolicy();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) { console.error(POLICY_ERRORS.SAVE_POLICY_ERROR, error); }
    };

    const handleInputChange = (key) => (e) => {
        let value = e.target.value;
        if (key === POLICY_KEYS.ID) {
            value = value.replace(/\s/g, "");
            value = `dp-${value.toLowerCase()}`;
        }
        setSelectedPolicy((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {successMessage &&
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchIvrPolicy}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={POLICY_STRINGS.SEARCH_POLICY}
                        onChange={e => setSearchIvrPolicy(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Button
                        startIcon={<Add />}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                        onClick={() => handleAddEditPolicyClick({})}
                    >
                        {POLICY_STRINGS.ADD_POLICY}
                    </Button>
                </Grid>
            </Grid>
            <PolicyTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                policy={policyToDisplay}
                rowsPerPage={rowsPerPage}
                columns={POLICY_TABLE_COLUMNS}
                isPolicyLoading={loadingPolicy}
                setRowsPerPage={setRowsPerPage}
                onEdit={handleAddEditPolicyClick}
                handleRequestSort={handleRequestSort}
                filteredPolicyLength={filteredPolicyLength}
            />
            <AddEditPolicyDialog
                onSave={handleSavePolicy}
                isEditPolicy={isEditPolicy}
                selectedPolicy={selectedPolicy}
                open={isAddEditPolicyModalOpen}
                handleInputChange={handleInputChange}
                onClose={() => setIsAddEditPolicyModalOpen(false)}
            />
        </>
    );
};

export default Policy;